import { useEffect, useRef } from 'react';
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';

import {
  downloadRezeptInvoiceListPdf,
  fetchRezeptInvoiceList,
  type RezeptInvoiceListFilters,
  type RezeptInvoiceListResponse,
  type RezeptInvoiceListPdfPayload,
} from '@/services/main/rezept';
import { useClinicContext } from './useClinicContext';

const LIST_KEY = ['rezept-invoice', 'list'] as const;

/**
 * Wait between two looks at the server: quick at first (a few rows render in 1–3 s),
 * then backing off so a tab the user never opened does not hammer the API.
 */
export const PRINT_CONFIRM_DELAYS_MS = [1000, 1000, 1500, 2000, 3000, 5000];
/** Give up after the backend's PDF time limit (ClinicConstants::PDF_TIME_LIMIT = 300 s). */
export const PRINT_CONFIRM_TIMEOUT_MS = 300_000;

/** 印刷 column rule (RezeptInvoiceListTable): 済 when print_invoice is set and not -1. */
const isPrinted = (v: number | null | undefined) => v != null && v !== -1;

const sleep = (ms: number, signal: AbortSignal) =>
  new Promise<void>(resolve => {
    const t = setTimeout(resolve, ms);
    signal.addEventListener('abort', () => {
      clearTimeout(t);
      resolve();
    });
  });

/**
 * HIT-14678: show 「済」 for the printed rows as soon as the SERVER has recorded the print.
 *
 * POST /rezepts/invoice-list-pdf only issues a preview ticket; print_invoice is written while the
 * new tab's GET /rezepts/invoice-list-pdf/preview renders the PDF. So right after the POST the
 * list API still answers "not printed", and a single refetch at that moment (the old behaviour)
 * cached that stale answer — 「済」 stayed hidden until a manual reload.
 *
 * Instead, re-read the visible list pages in the background until every printed row that was
 * still 未印刷 comes back printed (or the PDF time limit passes), writing each server answer into
 * the cache. 「済」 therefore only ever comes from the server; nothing is assumed client-side.
 * The direct fetch + setQueryData keeps the table from flashing its loading overlay each round.
 */
export async function confirmPrintRecorded(
  queryClient: QueryClient,
  rezeptIds: number[],
  signal: AbortSignal
): Promise<void> {
  const printedIds = new Set(rezeptIds.map(Number));
  const activeQueries = () => queryClient.getQueryCache().findAll({ queryKey: LIST_KEY, type: 'active' });

  // Rows on screen that were not 済 when 印刷 was clicked: these are the ones to wait for.
  const pending = new Set<number>();
  activeQueries().forEach(q => {
    const rows = (q.state.data as RezeptInvoiceListResponse | undefined)?.data?.data ?? [];
    rows.forEach(row => {
      if (printedIds.has(Number(row.id)) && !isPrinted(row.print_invoice)) pending.add(Number(row.id));
    });
  });

  const deadline = Date.now() + PRINT_CONFIRM_TIMEOUT_MS;
  try {
    for (let round = 0; pending.size > 0 && Date.now() < deadline; round++) {
      await sleep(PRINT_CONFIRM_DELAYS_MS[Math.min(round, PRINT_CONFIRM_DELAYS_MS.length - 1)], signal);
      if (signal.aborted) return;

      const stillShown = new Set<number>();
      for (const q of activeQueries()) {
        const [, , tenant, clinicNumber, , filters] = q.queryKey as [
          string,
          string,
          string,
          string,
          string,
          RezeptInvoiceListFilters,
        ];
        if (!filters) continue;
        let fresh: RezeptInvoiceListResponse;
        try {
          fresh = await fetchRezeptInvoiceList(tenant, clinicNumber, filters);
        } catch {
          continue; // transient error: try again next round
        }
        if (signal.aborted) return;
        queryClient.setQueryData(q.queryKey, fresh);
        (fresh?.data?.data ?? []).forEach(row => {
          if (pending.has(Number(row.id)) && !isPrinted(row.print_invoice)) stillShown.add(Number(row.id));
        });
      }
      // Done with rows the server now reports printed, or that are no longer on screen.
      [...pending].forEach(id => {
        if (!stillShown.has(id)) pending.delete(id);
      });
    }
  } finally {
    // Other cached pages / filters: refetch when they are shown next, without a spinner now.
    queryClient.invalidateQueries({ queryKey: LIST_KEY, refetchType: 'none' });
  }
}

/**
 * Mutation hook for 領収書・請求書 `一覧印刷` (bulk receipt/invoice PDF).
 * Returns a preview URL; caller is responsible for opening it in a new window.
 * On success, follows the server until it has recorded the print (see confirmPrintRecorded).
 */
export const useRezeptInvoiceListPdf = () => {
  const { tenant, clinicNumber } = useClinicContext();
  const queryClient = useQueryClient();
  const confirmRef = useRef<AbortController | null>(null);

  useEffect(() => () => confirmRef.current?.abort(), []);

  return useMutation({
    mutationFn: (payload: RezeptInvoiceListPdfPayload) =>
      downloadRezeptInvoiceListPdf(tenant ?? '', clinicNumber ?? '', payload),
    onSuccess: (_url, payload) => {
      confirmRef.current?.abort();
      const controller = new AbortController();
      confirmRef.current = controller;
      void confirmPrintRecorded(queryClient, payload.rezept_ids ?? [], controller.signal);
    },
  });
};

export default useRezeptInvoiceListPdf;
